import "@react-pdf-viewer/core/lib/styles/index.css";
import { Viewer, Worker, SpecialZoomLevel } from "@react-pdf-viewer/core";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import packageJson from "../../../package.json";

import "@react-pdf-viewer/core/lib/styles/index.css";

import { useTranslation } from "react-i18next";
import { clsx } from "clsx";
import { useEffect, useRef, useState } from "react";

import LoadingWithText from "src/components/LoadingWithText/LoadingWithText";
import { MdPictureAsPdf } from "react-icons/md";
import ButtonGroup from "src/components/ButtonGroup/ButtonGroup";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "src/components/Button/Button.types";

// Zoom config
const ZOOM_SENSITIVITY = 1.4;
const MIN_ZOOM = 1.0;
const MAX_ZOOM = 3.0;
const DOUBLE_TAP_ZOOM = 2.0;

const pdfjsVersion = packageJson.dependencies["pdfjs-dist"];

interface ITransactionPdfSlide {
  url: string;
}

const TransactionPdfSlide: React.FC<ITransactionPdfSlide> = ({ url }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const currentZoom = useRef(1.0);
  const lastTap = useRef<number>(0);
  const [isLoaded, setIsLoaded] = useState(false);

  const zoomPluginInstance = zoomPlugin();
  const { zoomTo } = zoomPluginInstance;

  useEffect(() => {
    currentZoom.current = MIN_ZOOM;
    setIsLoaded(false); // Reset al cambiar de PDF
  }, [url]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let startDistance = 0;

    const getDistance = (touches: TouchList) => {
      const [t1, t2] = [touches[0], touches[1]];
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const now = Date.now();
        const delta = now - lastTap.current;

        if (delta < 300) {
          const newZoom =
            currentZoom.current >= DOUBLE_TAP_ZOOM ? MIN_ZOOM : DOUBLE_TAP_ZOOM;
          zoomTo(newZoom);
          currentZoom.current = newZoom;
        }

        lastTap.current = now;
      }

      if (e.touches.length === 2) {
        startDistance = getDistance(e.touches);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const newDistance = getDistance(e.touches);
        const scale = newDistance / startDistance;
        const newZoom = Math.min(
          Math.max(
            currentZoom.current * Math.pow(scale, ZOOM_SENSITIVITY),
            MIN_ZOOM,
          ),
          MAX_ZOOM,
        );
        zoomTo(newZoom);
        currentZoom.current = newZoom;
      }
    };

    const handleDoubleClick = () => {
      const newZoom =
        currentZoom.current >= DOUBLE_TAP_ZOOM ? MIN_ZOOM : DOUBLE_TAP_ZOOM;
      zoomTo(newZoom);
      currentZoom.current = newZoom;
    };

    const handleWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.stopPropagation();
      }
    };

    container.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("dblclick", handleDoubleClick);
    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("dblclick", handleDoubleClick);
      container.removeEventListener("wheel", handleWheel);
    };
  }, [zoomTo]);

  return (
    <div
      ref={containerRef}
      className={clsx(
        "flex justify-center items-start w-full h-full overflow-auto !bg-transparent",
      )}
    >
      <Worker
        workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.js`}
      >
        {!isLoaded && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-transparent text-white">
            <LoadingWithText />
          </div>
        )}

        <div className={clsx("w-full h-full", { invisible: !isLoaded })}>
          <Viewer
            fileUrl={url}
            theme="dark"
            plugins={[zoomPluginInstance]}
            defaultScale={SpecialZoomLevel.PageFit}
            enableSmoothScroll
            onDocumentLoad={() => {
              setIsLoaded(true);
              const container = containerRef.current;
              if (container) {
                container.scrollTo({ top: 0 });
                zoomTo(MIN_ZOOM);
                currentZoom.current = MIN_ZOOM;
              }
            }}
            onDocumentAskPassword={() => {
              setIsLoaded(true);
            }}
            renderError={(error) => {
              setIsLoaded(true);
              return (
                <div className="text-white text-sm flex flex-col items-center justify-center min-h-full gap-4">
                  <MdPictureAsPdf
                    size={ICON_SIZES.XL}
                    className="text-blue-400 dark:text-purple-400"
                  />
                  <div>
                    {error.message?.includes("password")
                      ? t("PDF protegido con contraseña")
                      : "Error al cargar el PDF"}
                  </div>
                  {!!error.message?.includes("password") && (
                    <ButtonGroup
                      buttons={[
                        {
                          text: "Desbloquear",
                          onClick: () => {},
                          kind: EButtonKind.SAVE,
                        },
                      ]}
                      align="center"
                    />
                  )}
                </div>
              );
            }}
          />
        </div>
      </Worker>
    </div>
  );
};

export default TransactionPdfSlide;
